import { Clock3 } from 'lucide-react'
import { useMemo } from 'react'
import type { GraphViewNode } from '@/types/graph-view'
import { cn } from '@/utils/cn'

interface TimelineMiniPreviewProps {
  nodes: GraphViewNode[]
  className?: string
}

const TS_KEYS = [
  'event_time',
  'occurred_at',
  'observed_at',
  'transaction_time',
  'start_time',
  'end_time',
  'datetime',
  'timestamp',
  'created_at',
  'updated_at',
] as const

const VB_W = 200
const VB_H = 138
const PAD_X = 12
const AXIS_Y = VB_H - 22
const BAR_AREA_TOP = 24

/**
 * Mini timeline preview — extracts temporal properties off graph-view nodes
 * and renders a binned bar histogram with auto-chosen granularity. Each bar
 * is sized by event density and the time span is summarised in a chip next
 * to the title.
 *
 * The bar bins use whichever of {hour, day, week, month} produces a readable
 * resolution so the chart adapts to the investigation window without ever
 * looking sparse or oversaturated.
 */
export function TimelineMiniPreview({ nodes, className }: TimelineMiniPreviewProps) {
  const view = useMemo(() => buildTimeline(nodes), [nodes])

  return (
    <div
      data-empty={view.total > 0 && view.range ? undefined : ''}
      className={cn(
        'relative overflow-hidden rounded-lg border border-border/40 bg-muted/30',
        className
      )}
    >
      <div className="absolute left-2 top-2 z-10 flex items-center gap-1 text-3xs font-medium uppercase tracking-wider text-muted-foreground">
        <Clock3 className="size-3" />
        Timeline
      </div>
      {view.total > 0 && view.range && (
        <div className="absolute right-2 top-2 z-10 rounded bg-background/70 px-1.5 py-0.5 font-mono text-4xs text-muted-foreground backdrop-blur">
          {view.spanLabel} · {view.granularity}
        </div>
      )}
      {view.total > 0 && view.range ? (
        <svg
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          className="block h-full w-full"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="timeline-bar" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="currentColor" stopOpacity={0.95} />
              <stop offset="100%" stopColor="currentColor" stopOpacity={0.35} />
            </linearGradient>
          </defs>

          {/* Baseline + subtle dashed gridlines */}
          <g className="text-border" stroke="currentColor" strokeWidth={0.4}>
            <line x1={PAD_X} x2={VB_W - PAD_X} y1={AXIS_Y} y2={AXIS_Y} strokeOpacity={0.9} />
            {[0.25, 0.5, 0.75].map(t => (
              <line
                key={t}
                x1={PAD_X}
                x2={VB_W - PAD_X}
                y1={AXIS_Y - (AXIS_Y - BAR_AREA_TOP) * t}
                y2={AXIS_Y - (AXIS_Y - BAR_AREA_TOP) * t}
                strokeDasharray="1 2"
                strokeOpacity={0.35}
              />
            ))}
          </g>

          {/* Bars */}
          <g className="text-primary" fill="url(#timeline-bar)">
            {view.bars.map((b, i) => (
              <rect
                key={i}
                x={b.x}
                y={AXIS_Y - b.h}
                width={b.w}
                height={b.h}
                rx={b.w > 2 ? 0.8 : 0}
                ry={b.w > 2 ? 0.8 : 0}
              />
            ))}
          </g>

          {/* Peak marker */}
          {view.peak && (
            <g className="text-warning">
              <line
                x1={view.peak.x + view.peak.w / 2}
                x2={view.peak.x + view.peak.w / 2}
                y1={AXIS_Y - view.peak.h - 3}
                y2={AXIS_Y - view.peak.h - 8}
                stroke="currentColor"
                strokeWidth={0.8}
              />
              <text
                x={view.peak.x + view.peak.w / 2}
                y={AXIS_Y - view.peak.h - 10}
                textAnchor="middle"
                fontSize={7}
                className="font-mono"
                fill="currentColor"
              >
                {view.peak.count}
              </text>
            </g>
          )}

          {/* Axis labels */}
          <g
            className="font-mono text-muted-foreground"
            fill="currentColor"
            fontSize={5}
            opacity={0.9}
          >
            <text x={PAD_X} y={AXIS_Y + 8}>
              {formatAxisLabel(view.range.min, view.granularity)}
            </text>
            <text x={VB_W / 2} y={AXIS_Y + 8} textAnchor="middle">
              {view.midLabel}
            </text>
            <text x={VB_W - PAD_X} y={AXIS_Y + 8} textAnchor="end">
              {formatAxisLabel(view.range.max, view.granularity)}
            </text>
          </g>
        </svg>
      ) : (
        <div className="flex h-full w-full items-center justify-center text-2xs text-muted-foreground">
          No temporal data
        </div>
      )}
      <div className="absolute bottom-1.5 right-2 z-10 rounded bg-background/70 px-1.5 py-0.5 font-mono text-3xs text-muted-foreground backdrop-blur">
        {view.total} events
      </div>
    </div>
  )
}

interface Bar {
  x: number
  w: number
  h: number
  count: number
}

interface TimelineView {
  bars: Bar[]
  peak: Bar | null
  range: { min: Date; max: Date } | null
  total: number
  spanLabel: string
  midLabel: string
  granularity: string
}

const MS_HOUR = 60 * 60 * 1000
const MS_DAY = 24 * MS_HOUR
const MS_WEEK = 7 * MS_DAY
const MS_MONTH = 30 * MS_DAY

function buildTimeline(nodes: GraphViewNode[]): TimelineView {
  const timestamps: number[] = []
  for (const node of nodes) {
    const props = node.properties ?? {}
    for (const key of TS_KEYS) {
      const raw = props[key]
      if (!raw) continue
      const time = parseTime(raw)
      if (time != null) {
        timestamps.push(time)
        break
      }
    }
  }
  if (timestamps.length === 0) {
    return {
      bars: [],
      peak: null,
      range: null,
      total: 0,
      spanLabel: '',
      midLabel: '',
      granularity: '',
    }
  }

  const min = Math.min(...timestamps)
  const max = Math.max(...timestamps)
  const span = Math.max(max - min, 1)
  const { bucketMs, granularity } = pickGranularity(span)
  const bucketCount = Math.max(8, Math.min(40, Math.ceil(span / bucketMs)))
  const realBucket = span / bucketCount

  const buckets = new Array(bucketCount).fill(0)
  for (const t of timestamps) {
    const idx = Math.min(bucketCount - 1, Math.floor((t - min) / realBucket))
    buckets[idx] += 1
  }
  const maxBucket = Math.max(...buckets, 1)

  const usableW = VB_W - PAD_X * 2
  const usableH = AXIS_Y - BAR_AREA_TOP
  const slotW = usableW / bucketCount
  const barGap = Math.min(0.6, slotW * 0.18)
  const barW = Math.max(0.8, slotW - barGap)

  let peak: Bar | null = null
  const bars: Bar[] = buckets.map((count, i) => {
    const h = count === 0 ? 0 : Math.max(1.4, (count / maxBucket) * usableH)
    const x = PAD_X + i * slotW + barGap / 2
    const bar: Bar = { x, w: barW, h, count }
    if (!peak || count > peak.count) peak = bar
    return bar
  })
  // Don't surface peak marker for trivial peaks (single event spread).
  if (peak && (peak.count < 2 || maxBucket < 2)) peak = null

  return {
    bars,
    peak,
    range: { min: new Date(min), max: new Date(max) },
    total: timestamps.length,
    spanLabel: formatSpan(span),
    midLabel: formatAxisLabel(new Date(min + span / 2), granularity),
    granularity,
  }
}

function pickGranularity(span: number): { bucketMs: number; granularity: string } {
  if (span < MS_DAY) return { bucketMs: MS_HOUR, granularity: 'hourly' }
  if (span < 60 * MS_DAY) return { bucketMs: MS_DAY, granularity: 'daily' }
  if (span < 2 * 365 * MS_DAY) return { bucketMs: MS_WEEK, granularity: 'weekly' }
  return { bucketMs: MS_MONTH, granularity: 'monthly' }
}

function parseTime(raw: unknown): number | null {
  if (raw == null) return null
  if (raw instanceof Date) return raw.getTime()
  if (typeof raw === 'number') return raw > 1e12 ? raw : raw * 1000
  if (typeof raw === 'string') {
    const ms = Date.parse(raw)
    return Number.isFinite(ms) ? ms : null
  }
  return null
}

function formatShort(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
}

function formatAxisLabel(d: Date, granularity: string): string {
  const date = formatShort(d)
  if (granularity === 'hourly') {
    const hh = String(d.getUTCHours()).padStart(2, '0')
    const mm = String(d.getUTCMinutes()).padStart(2, '0')
    return `${date} ${hh}:${mm}`
  }
  return date
}

function formatSpan(ms: number): string {
  if (ms < MS_HOUR) return `${Math.max(1, Math.round(ms / 60000))}m`
  if (ms < MS_DAY) return `${Math.round(ms / MS_HOUR)}h`
  if (ms < 60 * MS_DAY) return `${Math.round(ms / MS_DAY)}d`
  if (ms < 2 * 365 * MS_DAY) return `${Math.round(ms / MS_WEEK)}w`
  return `${(ms / (365 * MS_DAY)).toFixed(1)}y`
}
